/**
 * PSEA scoring rules, mirroring the server-side rules in
 * cims-backend/src/api/psea-assessment/pseaScoring.ts.
 *
 * Each of the 8 core standards is worth one point, awarded when the standard
 * is explicitly met ("Yes") or does not apply to the organization ("Not
 * applicable", offered on standards 2 and 8 only). "No" and unanswered both
 * score zero. The backend recomputes and persists the rating on save; this
 * copy exists so the form can show the score updating live as the user answers.
 */
import { PSEA_CORE_STANDARDS } from "./pseaCoreStandards";

export const PSEA_TOTAL_STANDARDS = 8;

export const PSEA_RATING_FULL = "Full Capacity";
export const PSEA_RATING_MEDIUM = "Medium Capacity";
export const PSEA_RATING_LOW = "Low Capacity";

/**
 * How a standard was answered. "Not applicable" is stored on its own
 * `notApplicable` boolean rather than as a third value of `meetsStandard`,
 * which is a boolean column on the backend; a standard marked not applicable
 * stores `meetsStandard: null`. Reading the answer through this helper keeps
 * that two-field encoding in one place.
 */
export type PseaAnswer = "yes" | "no" | "na" | null;

/**
 * Whether "Not applicable" may be chosen on a standard, read from the standard
 * definitions so the form and the scoring cannot drift apart. The backend
 * enforces the same list in its own pseaScoring.ts and is the authority — it
 * ignores `notApplicable` on any other standard, so honouring it here keeps the
 * live score in step with the saved one.
 */
export const allowsNotApplicable = (standardNumber: any): boolean =>
  PSEA_CORE_STANDARDS.some(
    (s) => s.number === Number(standardNumber) && s.allowNotApplicable === true
  );

export const pseaAnswer = (standard: any): PseaAnswer => {
  if (
    standard?.notApplicable === true &&
    allowsNotApplicable(standard?.standardNumber)
  )
    return "na";
  if (standard?.meetsStandard === true) return "yes";
  if (standard?.meetsStandard === false) return "no";
  return null;
};

/** A standard scores its point when it is met or marked not applicable. */
export const isStandardScored = (standard: any): boolean => {
  const answer = pseaAnswer(standard);
  return answer === "yes" || answer === "na";
};

/** Number of standards scoring a point. */
export const computePseaScore = (coreStandards: any[]): number =>
  (coreStandards || []).reduce(
    (total: number, standard: any) =>
      isStandardScored(standard) ? total + 1 : total,
    0
  );

/** All 8 standards met is Full Capacity; 6-7 Medium; 5 or fewer Low. */
export const pseaRatingFromScore = (score: number): string => {
  if (score >= PSEA_TOTAL_STANDARDS) return PSEA_RATING_FULL;
  if (score >= 6) return PSEA_RATING_MEDIUM;
  return PSEA_RATING_LOW;
};

/** Bootstrap colour for a rating, used by badges and the live score panel. */
export const pseaRatingColor = (rating?: string | null): string => {
  if (rating === PSEA_RATING_FULL) return "success";
  if (rating === PSEA_RATING_MEDIUM) return "warning";
  if (rating === PSEA_RATING_LOW) return "danger";
  return "secondary";
};

/** How many standards have been answered, in any of the three ways. */
export const countAnswered = (coreStandards: any[]): number =>
  (coreStandards || []).filter(
    (standard: any) => pseaAnswer(standard) !== null
  ).length;
